import { ethers } from 'ethers';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { decodeEventLog } from 'viem';
import { CPGABI, ERC20ABI } from '../../ABI/ABI';
import Loader from '../../components/loader/Loader';
import CustomMilestoneScheduleEditor from '../../components/milestoneDuration/CustomMilestoneScheduleEditor';
import MilestoneSchedulePreview from '../../components/milestoneDuration/MilestoneSchedulePreview';
import Token from '../../components/tokenList/Token';
import ThemedSelect from '../../components/ui/ThemedSelect';
import { mileStoneDuration } from '../../config/data';
import {
    CPGAddress,
    targetChainId,
    ZERO_ADDRESS,
} from '../../constant/constant';
import {
    CONTRACT_ADDRESS_WARNING_MSG,
    INITIATE_TRACKED_PAYMENT_SUCCESS,
    SELF_PAYMENT_WARNING_MSG,
    TRANSACTION_ERROR_MSG,
    USER_REJECT_TRANSACTION_MSG,
} from '../../constant/toasterMessage';
import {
    convertBigToSmallDigitWithSpecificDecimal,
    convertInTokenBigAmount,
    fireErrorToast,
    fireSuccessToast,
    getAllowance,
    handleKeyDown,
    handleWheel,
    isContractAddress,
} from '../../utils/commonFunction';
import { ContractWrite } from '../../utils/contractInstance';
import { publicClient } from '../../utils/wagmiConfig';
import { persistPaymentRecord } from '../../api/backendClient';
import {
    decodeLockedPaymentCreatedId,
    fetchLockedEscrowFeeBps,
    paymentRefBytes32,
} from '../../utils/cpgDiamondReader';
import {
    buildCustomMilestoneSchedule,
    customRowsToSchedulePayload,
} from '../../utils/milestoneSchedule';
import { readContractData } from '../../utils/contractInstance';

const TrackedPayment = ({ embedded = false, basics = null }) => {
    const [milestoneCount, setMilestoneCount] = useState('');
    const [milestoneDuration, setMilestoneDuration] = useState(
        mileStoneDuration[0].timestamp,
    );
    const [trakedPaymentData, setTrakedPaymentData] = useState({
        userName: '',
        amount: '',
        releaseDate: '',
    });
    const [token, setToken] = useState({});
    const [note, setNote] = useState('');
    const [scheduleMode, setScheduleMode] = useState('equal');
    const [customRows, setCustomRows] = useState([]);
    const [customValidation, setCustomValidation] = useState({ ok: false });
    const [feeBps, setFeeBps] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isRecipientContract, setIsRecipientContract] = useState(false);
    const [buttonDetail, setButtonDetail] = useState({
        name: 'Enter Detail',
        bgColor: 'bg-gray-600',
        isDisabled: false,
        textColor: 'text-gray-600',
    });

    const pendingPaymentRef = useRef(null);
    const pendingLockedWriteRef = useRef(null);
    const recordedTxRef = useRef(null);
    const { address: walletAddress } = useAccount();
    const { signMessageAsync } = useSignMessage();

    useEffect(() => {
        if (!embedded || !basics) return;
        setTrakedPaymentData((prev) => ({
            ...prev,
            userName: basics.userName || '',
            amount: basics.amount || '',
        }));
        if (basics.token?.address) {
            setToken(basics.token);
        }
    }, [
        embedded,
        basics?.userName,
        basics?.amount,
        basics?.token?.address,
    ]);

    const pendingPaymentStorageKey = walletAddress
        ? `cronpay_pending_tracked_${walletAddress.toLowerCase()}`
        : null;

    const readPendingPaymentSnapshot = () => {
        if (pendingPaymentRef.current) return pendingPaymentRef.current;
        if (!pendingPaymentStorageKey) return null;
        try {
            const raw = sessionStorage.getItem(pendingPaymentStorageKey);
            return raw ? JSON.parse(raw) : null;
        } catch (_error) {
            return null;
        }
    };

    const writePendingPaymentSnapshot = (snapshot) => {
        pendingPaymentRef.current = snapshot;
        if (!pendingPaymentStorageKey || !snapshot) return;
        try {
            sessionStorage.setItem(
                pendingPaymentStorageKey,
                JSON.stringify(snapshot),
            );
        } catch (_error) {
            // ignore
        }
    };

    const clearPendingPaymentSnapshot = () => {
        pendingPaymentRef.current = null;
        if (!pendingPaymentStorageKey) return;
        try {
            sessionStorage.removeItem(pendingPaymentStorageKey);
        } catch (_error) {
            // ignore
        }
    };

    const handleCustomValidationChange = useCallback((validation) => {
        setCustomValidation(validation);
    }, []);

    useEffect(() => {
        if (!CPGAddress) return;
        fetchLockedEscrowFeeBps(readContractData, CPGAddress, CPGABI).then(setFeeBps);
    }, []);

    const customSchedulePreview = useMemo(() => {
        if (scheduleMode !== 'custom' || !customValidation.ok || !token.decimals) {
            return null;
        }
        const payload = customRowsToSchedulePayload(customRows, token.decimals);
        if (!payload) return null;
        return buildCustomMilestoneSchedule({
            unlockTimesSec: payload.unlockTimesSec,
            milestoneAmounts: payload.milestoneAmountsWei.map((wei) =>
                Number(ethers.formatUnits(wei, token.decimals)),
            ),
        });
    }, [scheduleMode, customValidation.ok, customRows, token.decimals]);

    const buildLockedWriteCall = () => {
        const trackedAmount = ethers.parseUnits(
            trakedPaymentData.amount.toString(),
            token?.decimals,
        );
        const ref = paymentRefBytes32(note);

        if (scheduleMode === 'custom') {
            const payload = customRowsToSchedulePayload(customRows, token.decimals);
            if (!payload || !customValidation.ok) return null;
            return {
                functionName: 'initiateLockedPaymentWithSchedule',
                args: [
                    trakedPaymentData.userName,
                    token?.address,
                    trackedAmount.toString(),
                    payload.unlockTimesSec,
                    payload.milestoneAmountsWei,
                    ref,
                ],
            };
        }

        return {
            functionName: 'initiateLockedPayment',
            args: [
                trakedPaymentData.userName,
                token?.address,
                trackedAmount.toString(),
                milestoneCount,
                milestoneDuration.toString(),
                ref,
            ],
        };
    };

    const executeLockedPaymentWrite = (call) => {
        if (!call) return;
        lockedPayment({
            abi: CPGABI,
            address: CPGAddress,
            functionName: call.functionName,
            args: call.args,
        });
    };

    const {
        writeContract: approve,
        writeSuccessResponse: aprroveResponse,
        writeErrorResponse: approveErrorResponse,
    } = ContractWrite({
        address: token?.address,
        abi: ERC20ABI,
        functionName: 'approve',
        initialArgs: [CPGAddress, 0n],
    });

    useEffect(() => {
        if (aprroveResponse && pendingLockedWriteRef.current) {
            executeLockedPaymentWrite(pendingLockedWriteRef.current);
            pendingLockedWriteRef.current = null;
        }
        // eslint-disable-next-line
    }, [aprroveResponse]);

    useEffect(() => {
        if (approveErrorResponse) {
            onWriteError(approveErrorResponse);
        }
        // eslint-disable-next-line
    }, [approveErrorResponse]);

    // **********************************************************Approve Payment********************************************************************* //

    //******************************************************************** Smart Contract Call ************************************************************************//
    const {
        writeContract: lockedPayment,
        writeSuccessResponse: lockedPaymentResponse,
        writeErrorResponse: lockedPaymentErrorResponse,
        updateArgs: lockedPaymentArgs,
        transactionHash: lockedPaymentTxHash,
    } = ContractWrite({
        address: CPGAddress,
        abi: CPGABI,
        functionName: 'initiateLockedPayment',
        initialArgs: [
            trakedPaymentData.userName,
            token?.address ?? ZERO_ADDRESS,
            trakedPaymentData.amount ?? 1,
            milestoneCount,
            milestoneDuration.toString(),
            paymentRefBytes32(note),
        ],
        enabled: true,
    });

    const onWriteError = (error) => {
        if (error) {
            if (
                !error?.details?.includes(
                    'MetaMask Tx Signature: User denied transaction signature',
                )
            ) {
                fireErrorToast(TRANSACTION_ERROR_MSG);
            }
            if (
                error?.details?.includes(
                    'MetaMask Tx Signature: User denied transaction signature',
                )
            ) {
                fireErrorToast(USER_REJECT_TRANSACTION_MSG);
            }
        }
        setLoading(false);
        // }
    };

    const persistTrackedPayment = async (txHash) => {
        const snapshot = readPendingPaymentSnapshot();
        if (!snapshot || !txHash || !walletAddress) {
            console.warn(
                'Skipped backend save for tracked payment:',
                !snapshot ? 'missing payment snapshot' : !txHash ? 'missing tx hash' : 'wallet disconnected',
            );
            if (!txHash) {
                fireErrorToast(
                    'Payment confirmed on-chain but transaction hash was missing. Refresh and check Ongoing payments.',
                );
            }
            return;
        }
        if (recordedTxRef.current === txHash) return;
        recordedTxRef.current = txHash;

        try {
            let onChainPaymentId = null;
            let blockNumber = null;
            try {
                const receipt = await publicClient.waitForTransactionReceipt({
                    hash: txHash,
                });
                blockNumber =
                    receipt?.blockNumber !== undefined &&
                    receipt?.blockNumber !== null
                        ? receipt.blockNumber.toString()
                        : null;

                for (const log of receipt?.logs || []) {
                    try {
                        const decoded = decodeEventLog({
                            abi: CPGABI,
                            data: log.data,
                            topics: log.topics,
                        });
                        onChainPaymentId = decodeLockedPaymentCreatedId(decoded);
                        if (onChainPaymentId) break;
                    } catch (_decodeError) {
                        // Non-CPG log; ignore.
                    }
                }
            } catch (decodeError) {
                console.warn(
                    'Failed to decode tracked payment receipt for paymentId:',
                    decodeError?.message,
                );
            }

            const result = await persistPaymentRecord(
                walletAddress,
                {
                    kind: 'tracked',
                    paymentId: onChainPaymentId,
                    txHash,
                    sender: walletAddress,
                    receiver: snapshot.receiver,
                    tokenAddress: snapshot.tokenAddress,
                    tokenSymbol: snapshot.tokenSymbol || null,
                    tokenDecimals: snapshot.tokenDecimals ?? null,
                    amount: snapshot.amount,
                    amountWei: snapshot.amountWei,
                    chainId: targetChainId,
                    blockNumber,
                    status: 'confirmed',
                    milestoneCount: snapshot.milestoneCount,
                    milestoneInterval: snapshot.milestoneInterval,
                    meta: {
                        ...(snapshot.note ? { note: snapshot.note } : {}),
                        ...(snapshot.customSchedule
                            ? {
                                  customSchedule: true,
                                  unlockTimes: snapshot.unlockTimes,
                                  milestoneAmounts: snapshot.milestoneAmounts,
                              }
                            : {}),
                    },
                },
                { signMessageAsync },
            );

            if (!result.ok) {
                console.warn('Failed to persist tracked payment to backend:', result.reason);
                fireErrorToast(
                    `Payment succeeded on-chain but was not saved to the server. ${result.reason}`,
                );
            }
        } catch (error) {
            console.warn(
                'Failed to persist tracked payment to backend:',
                error?.response?.data?.message || error?.message,
            );
            fireErrorToast(
                'Payment succeeded on-chain but could not be saved to the server.',
            );
        } finally {
            clearPendingPaymentSnapshot();
        }
    };

    useEffect(() => {
        if (!lockedPaymentResponse || !lockedPaymentTxHash) return;

        persistTrackedPayment(lockedPaymentTxHash);
        setTrakedPaymentData({ userName: '', amount: '', releaseDate: '' });
        setMilestoneCount('');
        setToken({});
        setNote('');
        setMilestoneDuration(mileStoneDuration[0].timestamp);
        setScheduleMode('equal');
        setCustomRows([]);
        setLoading(false);
        fireSuccessToast(INITIATE_TRACKED_PAYMENT_SUCCESS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lockedPaymentResponse, lockedPaymentTxHash]);

    useEffect(() => {
        if (scheduleMode !== 'equal' || !token.decimals || !milestoneCount) return;
        const trackedAmount = ethers.parseUnits(
            trakedPaymentData.amount,
            token?.decimals,
        );
        lockedPaymentArgs([
            trakedPaymentData.userName,
            token?.address ?? ZERO_ADDRESS,
            trackedAmount.toString(),
            milestoneCount,
            milestoneDuration.toString(),
            paymentRefBytes32(note),
        ]);
        // eslint-disable-next-line
    }, [trakedPaymentData, token, milestoneDuration, milestoneCount, note, scheduleMode]);

    useEffect(() => {
        if (lockedPaymentErrorResponse) {
            onWriteError(lockedPaymentErrorResponse);
        }
        // eslint-disable-next-line
    }, [lockedPaymentErrorResponse]);
    //******************************************************************** Smart Contract Call ************************************************************************//

    useEffect(() => {
        const checkAddress = async () => {
            if (ethers.isAddress(trakedPaymentData.userName)) {
                const result = await isContractAddress(
                    trakedPaymentData.userName,
                );
                setIsRecipientContract(result);
            } else {
                setIsRecipientContract(false);
            }
        };
        checkAddress();
    }, [trakedPaymentData.userName]);

    const handlAmountChange = (e) => {
        setMilestoneCount(e.target.value);
    };

    const handleChange = (e) => {
        setTrakedPaymentData({
            ...trakedPaymentData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSelectChange = (event) => {
        setMilestoneDuration(Number(event.target.value));
    };

    const handleSelectToken = (item) => {
        setToken(item);
    };

    const handleSubmit = async (e) => {
        try {
            setLoading(true);
            e.preventDefault();
            if (!ethers.isAddress(trakedPaymentData.userName)) {
                fireErrorToast('Enter a valid receiver wallet address');
                setLoading(false);
                return;
            }
            if (
                trakedPaymentData.userName.toLowerCase() ===
                walletAddress?.toLowerCase()
            ) {
                fireErrorToast(SELF_PAYMENT_WARNING_MSG);
                setLoading(false);
                return;
            }
            const isContract = await isContractAddress(
                trakedPaymentData.userName,
            );
            if (isContract) {
                fireErrorToast(CONTRACT_ADDRESS_WARNING_MSG);
                setLoading(false);
                return;
            }
            const allowance = await getAllowance(token.address, walletAddress);
            const convertedAmount = convertInTokenBigAmount(
                trakedPaymentData.amount,
                token.decimals,
            );

            const trackedAmountUnits = ethers.parseUnits(
                trakedPaymentData.amount.toString(),
                token.decimals,
            );
            const lockedCall = buildLockedWriteCall();
            if (!lockedCall) {
                fireErrorToast(
                    scheduleMode === 'custom'
                        ? 'Fix the custom milestone schedule before submitting'
                        : 'Enter payment details',
                );
                setLoading(false);
                return;
            }

            const customPayload =
                scheduleMode === 'custom'
                    ? customRowsToSchedulePayload(customRows, token.decimals)
                    : null;

            writePendingPaymentSnapshot({
                receiver: trakedPaymentData.userName,
                tokenAddress: token.address,
                tokenSymbol: token.symbol || token.name || null,
                tokenDecimals: token.decimals ?? null,
                amount: trakedPaymentData.amount,
                amountWei: trackedAmountUnits.toString(),
                milestoneCount:
                    scheduleMode === 'custom'
                        ? customPayload?.unlockTimesSec?.length ?? null
                        : Number(milestoneCount) || null,
                milestoneInterval:
                    scheduleMode === 'equal' && milestoneDuration
                        ? milestoneDuration.toString()
                        : null,
                customSchedule: scheduleMode === 'custom',
                unlockTimes: customPayload?.unlockTimesSec ?? null,
                milestoneAmounts: customPayload?.milestoneAmountsWei ?? null,
                note: note?.trim() || null,
            });
            recordedTxRef.current = null;

            if (Number(allowance) < Number(convertedAmount)) {
                pendingLockedWriteRef.current = lockedCall;
                approve({
                    abi: ERC20ABI,
                    address: token?.address,
                    functionName: 'approve',
                    args: [CPGAddress, trackedAmountUnits],
                });
            } else {
                executeLockedPaymentWrite(lockedCall);
            }
        } catch (error) {
            setLoading(false);
        } finally {
        }
    };

    useEffect(() => {
        let nameOfButton = 'Enter Detail';
        let buttonColor = 'bg-gray-300';
        let textColor = 'text-gray-600';
        let disable = true;
        if (trakedPaymentData.amount && token.decimals) {
            const balance = convertBigToSmallDigitWithSpecificDecimal(
                token.balance,
                token.decimals,
            );
            if (Number(trakedPaymentData.amount) > Number(balance)) {
                nameOfButton = 'Insufficient Balance';
                buttonColor = 'bg-red-700';
                textColor = 'text-white';
                disable = true;
            } else if (
                trakedPaymentData.userName &&
                trakedPaymentData.userName.toLowerCase() ===
                    walletAddress?.toLowerCase()
            ) {
                nameOfButton = 'Cannot pay to your own address';
                buttonColor = 'bg-red-700';
                textColor = 'text-white';
                disable = true;
            } else if (isRecipientContract) {
                nameOfButton = 'Cannot pay to contract address';
                buttonColor = 'bg-red-700';
                textColor = 'text-white';
                disable = true;
            } else if (!trakedPaymentData.userName) {
                nameOfButton = 'Enter Detail';
                buttonColor = 'bg-gray-300';
                textColor = 'text-gray-600';
                disable = true;
            } else if (scheduleMode === 'equal' && !milestoneCount) {
                nameOfButton = 'Enter milestone count';
                buttonColor = 'bg-gray-300';
                textColor = 'text-gray-600';
                disable = true;
            } else if (scheduleMode === 'custom' && !customValidation.ok) {
                nameOfButton = 'Fix custom schedule';
                buttonColor = 'bg-gray-300';
                textColor = 'text-gray-600';
                disable = true;
            } else {
                nameOfButton = 'Initiate Track Payment';
                buttonColor = 'bg-teal-700 hover:bg-teal-600';
                textColor = 'text-white';
                disable = false;
            }
        }
        setButtonDetail({
            name: nameOfButton,
            bgColor: buttonColor,
            textColor,
            isDisabled: disable,
        });
        // eslint-disable-next-line
    }, [
        trakedPaymentData,
        token,
        milestoneCount,
        isRecipientContract,
        scheduleMode,
        customValidation.ok,
    ]);

    const inputClass = 'cpg-input';
    const labelClass =
        'flex items-center gap-1 text-[13px] font-semibold text-zinc-900 dark:text-zinc-200';

    const tokenBalanceFormatted =
        token.decimals !== undefined
            ? convertBigToSmallDigitWithSpecificDecimal(
                  token.balance,
                  token.decimals,
              )
            : null;

    const isInvalidAddress =
        trakedPaymentData.userName &&
        !ethers.isAddress(trakedPaymentData.userName);
    const isFormReady =
        trakedPaymentData.userName &&
        trakedPaymentData.amount &&
        token.address &&
        (scheduleMode === 'equal'
            ? milestoneCount
            : customValidation.ok && customRows.length > 0);

    const onMaxClick = () => {
        if (!token.decimals || !token.balance) return;
        const max = convertBigToSmallDigitWithSpecificDecimal(
            token.balance,
            token.decimals,
        );
        setTrakedPaymentData((prev) => ({
            ...prev,
            amount: String(max ?? ''),
        }));
    };

    const handleScheduleModeChange = (mode) => {
        setScheduleMode(mode);
        if (mode === 'equal') {
            setCustomRows([]);
            setCustomValidation({ ok: false });
        } else {
            setCustomRows([]);
            setCustomValidation({ ok: false });
        }
    };

    return (
        <form className="space-y-5">
            {!embedded && (
                <>
                    {/* Receiver wallet address */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label htmlFor="user_name" className={labelClass}>
                                Receiver Wallet Address{' '}
                                <span className="text-rose-600 dark:text-rose-400">
                                    *
                                </span>
                            </label>
                            <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
                                EVM-compatible address only
                            </span>
                        </div>
                        <input
                            type="text"
                            name="userName"
                            value={trakedPaymentData.userName}
                            id="user_name"
                            onChange={handleChange}
                            placeholder="0x... (42 characters)"
                            autoComplete="off"
                            spellCheck={false}
                            className={`${inputClass} font-mono ${isInvalidAddress ? 'border-rose-500/50 focus:ring-rose-500/40 focus:border-rose-500/50' : ''}`}
                            required
                        />
                        {isInvalidAddress && (
                            <p className="mt-1.5 flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-300">
                                <i className="fa-solid fa-circle-exclamation text-[10px]" />
                                Address must be a valid 0x… EVM wallet.
                            </p>
                        )}
                    </div>

                    {/* Token + Total Amount */}
                    <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
                        <div>
                            <label className={labelClass}>
                                Token{' '}
                                <span className="text-rose-600 dark:text-rose-400">
                                    *
                                </span>
                            </label>
                            <div className="relative mt-2">
                                <Token
                                    handleSelectToken={handleSelectToken}
                                    token={token}
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label htmlFor="amount" className={labelClass}>
                                    Total Amount{' '}
                                    <span className="text-rose-600 dark:text-rose-400">
                                        *
                                    </span>
                                </label>
                                {token.name && tokenBalanceFormatted !== null && (
                                    <button
                                        type="button"
                                        onClick={onMaxClick}
                                        className="text-[11px] font-bold text-teal-800 transition-colors hover:text-teal-900 tabular-nums dark:text-teal-400 dark:hover:text-teal-300"
                                        title="Use max balance"
                                    >
                                        Max: {tokenBalanceFormatted}{' '}
                                        {token.symbol || ''}
                                    </button>
                                )}
                            </div>
                            <input
                                type="number"
                                name="amount"
                                min={0}
                                onWheel={handleWheel}
                                onKeyDown={handleKeyDown}
                                value={trakedPaymentData.amount}
                                id="amount"
                                onChange={handleChange}
                                placeholder="0.00"
                                className={`${inputClass} tabular-nums`}
                                required
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Milestone terms section */}
            <div>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                        <i className="fa-regular fa-clock text-xs text-teal-700 dark:text-teal-400" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-800 dark:text-teal-400">
                            Milestone Terms
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => handleScheduleModeChange('equal')}
                            className={`rounded-xl border px-3 py-1.5 text-[11px] font-semibold transition-all ${
                                scheduleMode === 'equal'
                                    ? 'border-teal-600 bg-teal-700 text-white dark:border-emerald-500/40 dark:bg-emerald-600 dark:text-white'
                                    : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900'
                            }`}
                        >
                            Equal intervals
                        </button>
                        <button
                            type="button"
                            onClick={() => handleScheduleModeChange('custom')}
                            className={`rounded-xl border px-3 py-1.5 text-[11px] font-semibold transition-all ${
                                scheduleMode === 'custom'
                                    ? 'border-teal-600 bg-teal-700 text-white dark:border-emerald-500/40 dark:bg-emerald-600 dark:text-white'
                                    : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900'
                            }`}
                        >
                            Custom schedule
                        </button>
                    </div>
                </div>

                {scheduleMode === 'equal' ? (
                    <>
                        <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
                            <div>
                                <label className={labelClass}>
                                    Milestone Duration{' '}
                                    <span className="text-rose-600 dark:text-rose-400">
                                        *
                                    </span>
                                </label>
                                <p className="mb-2 mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-500">
                                    Time between each milestone release.
                                </p>
                                <ThemedSelect
                                    value={milestoneDuration}
                                    onChange={handleSelectChange}
                                    options={mileStoneDuration.map((item) => ({
                                        value: item.timestamp,
                                        label: item.name,
                                    }))}
                                />
                            </div>
                            <div>
                                <label htmlFor="milestoneCount" className={labelClass}>
                                    Number of Milestones{' '}
                                    <span className="text-rose-600 dark:text-rose-400">
                                        *
                                    </span>
                                </label>
                                <p className="mb-2 mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-500">
                                    How many equal portions to split the total into.
                                </p>
                                <input
                                    type="number"
                                    value={milestoneCount}
                                    id="milestoneCount"
                                    min={0}
                                    onWheel={handleWheel}
                                    onKeyDown={handleKeyDown}
                                    placeholder="3"
                                    className={`${inputClass} tabular-nums`}
                                    required
                                    onChange={handlAmountChange}
                                />
                            </div>
                        </div>

                        <MilestoneSchedulePreview
                            totalAmount={trakedPaymentData.amount}
                            milestoneCount={milestoneCount}
                            intervalSeconds={milestoneDuration}
                            tokenSymbol={token.symbol || token.name || ''}
                            className="mt-4"
                        />
                    </>
                ) : (
                    <div className="mt-1 space-y-4">
                        <CustomMilestoneScheduleEditor
                            grossAmount={trakedPaymentData.amount}
                            feeBps={feeBps}
                            tokenDecimals={token.decimals}
                            tokenSymbol={token.symbol || token.name || ''}
                            rows={customRows}
                            onRowsChange={setCustomRows}
                            onValidationChange={handleCustomValidationChange}
                        />
                        {customSchedulePreview && (
                            <MilestoneSchedulePreview
                                customSchedule={customSchedulePreview}
                                tokenSymbol={token.symbol || token.name || ''}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Optional note */}
            <div>
                <label htmlFor="note" className={labelClass}>
                    Payment Note{' '}
                    <span className="font-normal text-zinc-500 dark:text-zinc-500">
                        (optional)
                    </span>
                </label>
                <textarea
                    id="note"
                    name="note"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Website redesign project — 3-month contract"
                    maxLength={280}
                    className={`${inputClass} resize-none mt-2`}
                />
            </div>

            {/* Escrow info banner */}
            <div className="flex items-start gap-2.5 rounded-xl border border-teal-200 bg-teal-50/90 px-3.5 py-3 dark:border-teal-900/50 dark:bg-teal-950/35">
                <i className="fa-solid fa-shield-halved mt-0.5 text-sm text-teal-700 dark:text-teal-400" />
                <p className="text-[12px] leading-relaxed text-zinc-700 dark:text-zinc-300">
                    <span className="font-semibold text-teal-900 dark:text-teal-300">
                        Escrow-protected.
                    </span>{' '}
                    Funds are locked in a smart contract and released only when
                    milestones are approved. Disputes can be raised on any
                    milestone.
                </p>
            </div>

            {/* Submit */}
            {loading ? (
                <div className="flex w-full items-center justify-center rounded-xl bg-zinc-100 py-3 text-sm font-semibold text-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-300">
                    <Loader stroke="#0d9488" size="22px" />
                </div>
            ) : isFormReady && !buttonDetail.isDisabled ? (
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-5 py-3 text-sm font-bold text-white shadow-md shadow-teal-900/25 transition-colors hover:bg-teal-600 dark:bg-teal-600 dark:hover:bg-teal-500"
                >
                    <i className="fa-solid fa-shield-halved text-xs" />
                    Create Tracked Payment
                </button>
            ) : (
                <button
                    type="button"
                    disabled
                    className={`inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold ${
                        buttonDetail.bgColor === 'bg-red-700'
                            ? 'border border-rose-500/30 bg-rose-500/15 text-rose-700 dark:text-rose-300'
                            : 'border border-zinc-200 bg-zinc-100 text-zinc-500 dark:border-zinc-700/60 dark:bg-zinc-800/70 dark:text-zinc-500'
                    }`}
                >
                    <i className="fa-solid fa-lock text-[11px]" />
                    {buttonDetail.name || 'Enter Detail'}
                </button>
            )}
        </form>
    );
};

export default TrackedPayment;
